import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { getLogoForAccount } from '@pages/Trade/TradingAccountInfo/utils/getLogoForAccount'
import { TradingAccountsOutletCtx } from '@pages/Traders-Room/Trading-Accounts/TradingAccounts.page'
import { TradeButtonLinkWrapper } from '@pages/Traders-Room/Trading-Accounts/components/TradeButtonLinkWrapper'
import { TradingAccountActionsDropdown } from '@pages/Traders-Room/Trading-Accounts/components/TradingAccountActionsDropdown'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Caption1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Button, IconButton } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { PageQuery } from '@utils/ApiClient'
import { CommonPaths } from '@utils/path-common'
import {
  TradingAccountRestrictions,
  isRestricted,
  isTradingAccountSuspended,
} from '@utils/trading-account.utils'
import useSensitiveDataFlag from '@utils/useSensitiveDataFlag'
import { WalletRestrictions, isRestricted as isWalletRestricted } from '@utils/wallet.utils'

import { PlatformType, isPlatformTypeDisabled } from '@model/PlatformTypeDto'
import {
  PlatformTypeEnum,
  getDisplayPlatformName,
  isMT4PlatformType,
  isMT5PlatformType,
  isTMTPlatformType,
  isTradingViewPlatformType,
  isWebTraderAvailable,
} from '@model/PlatformTypeEnum'
import {
  DemoTradingAccount,
  TradingAccount,
  hasRestrictions,
  isTradingAccountStatusCloseOnly,
} from '@model/TradingAccount'
import { WalletDto } from '@model/WalletDto'

import { useDispatchOpenChat } from '@hooks/useDispatchOpenChat'
import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingNav } from '@hooks/useFundingNav'
import { usePlatformTradeUrl } from '@hooks/usePlatformTradeUrl'

import { Box } from '@shared/components/Box'
import { CopyLink } from '@shared/components/CopyLink'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import { CreateTradingAccountCTA } from '../components/CreateTradingAccountCTA'
import { SwapFreeContainer } from '../components/SwapFreeContainer'

import styles from './LiveTradingAccountsCards.module.scss'

export interface LiveTradingAccountsCardsProps {
  tableQuery: { data: TradingAccount[]; isLoading: boolean }
  wallets: WalletDto[]
  pageQuery?: PageQuery
  setPageQuery?: (q: PageQuery) => void
  platformTypes: PlatformType[]
  onLeverageChange: (args: { tradingAccount: TradingAccount; restricted?: boolean }) => void
  onDisplayDynamicLeverageInfo?: () => void
}

interface AccountHeaderProps {
  tradingAccount: TradingAccount
  balance: string
  hasAllRestrictions: boolean
  isTradeDisabled: boolean
  isPlatformDisabled: boolean
  hasCampaign: boolean
}

const AccountHeader: FC<AccountHeaderProps> = ({
  tradingAccount,
  balance,
  hasAllRestrictions,
  hasCampaign,
  isPlatformDisabled,
  isTradeDisabled,
}) => {
  const { t } = useTranslation()

  return (
    <Box className={styles.balanceWrapper}>
      <Box className={styles.logoCircle}>{getLogoForAccount(tradingAccount)}</Box>
      <Box className={styles.balance}>
        <Box flex gap='x8' align='center'>
          {hasAllRestrictions && <WarningIcon size={20} color={'text-danger-high-em'} />}
          <Caption1 className={styles.accountName} isParagraph>
            {tradingAccount.nickname || tradingAccount.name}
          </Caption1>
        </Box>
        <Body1 className={styles.accountBalance} semibold>
          <SensitiveData>{balance}</SensitiveData>
        </Body1>
      </Box>
      {hasCampaign && (
        <Badge type='yellow' size='xxs' contrast='high'>
          {t('Trading Account.Campaign')}
        </Badge>
      )}
      {(isPlatformDisabled || isTradeDisabled) &&
        !isTradingAccountStatusCloseOnly(tradingAccount.status) && (
          <Badge type='yellow' size='xxs'>
            {t('Deactivated')}
          </Badge>
        )}
      {isTradingAccountStatusCloseOnly(tradingAccount.status) && (
        <Badge type='yellow' size='xxs' contrast='low'>
          {t('Trading Account.Close Only')}
        </Badge>
      )}
    </Box>
  )
}

interface AccountActionsProps {
  tradingAccount: TradingAccount
  isTradeDisabled: boolean
  isDepositDisabled: boolean
  isPlatformDisabled: boolean
  hasCampaign: boolean
  isTransferFromRestricted: boolean
  hasAllRestrictions: boolean
  getPlatformTradeUrl: (
    tradingAccount: TradingAccount | DemoTradingAccount | undefined
  ) => Promise<string | undefined>
  handleWebTraderModalVisibility: (tradingAccount: TradingAccount) => void
  navigate: ReturnType<typeof useNavigate>
  handleSetSelectedTradingAccount: (tradingAccount: TradingAccount) => void
  openResetPasswordModal: () => void
  handleChangePasswordRestricted: () => void
  handleResetPasswordRestricted: () => void
  handleChangeInvestorPasswordRestricted: () => void
}

const AccountActions: FC<AccountActionsProps> = ({
  tradingAccount,
  isTradeDisabled,
  isDepositDisabled,
  isPlatformDisabled,
  isTransferFromRestricted,
  handleWebTraderModalVisibility,
  navigate,
  handleChangePasswordRestricted,
  handleChangeInvestorPasswordRestricted,
  handleResetPasswordRestricted,
  handleSetSelectedTradingAccount,
  openResetPasswordModal,
}) => {
  const { t } = useTranslation()
  const { navigateToFunding } = useFundingNav()
  const tracker = useDepositTracker()

  const handleTransfer = () => {
    navigate(`/dashboard/traders-room/trading-accounts/${tradingAccount.id}/transfer`)
  }

  const handleDeposit = () => {
    if (tradingAccount.campaigns.length > 0) {
      tracker.start({ entry_point: 'campaign_ta', preset_ta_id: tradingAccount.id })
    } else {
      tracker.start({ entry_point: 'ta', preset_ta_id: tradingAccount.id })
    }
    navigateToFunding(`${CommonPaths.TradersRoomTradingAccounts}/${tradingAccount.id}/deposit`)
  }
  const handleResetPassword = () => {
    handleSetSelectedTradingAccount(tradingAccount)
    openResetPasswordModal()
  }
  return (
    <Box className={styles.actions} gap='x8'>
      <Box flex gap='x8' align='center'>
        <TradeButtonLinkWrapper
          disabled={isTradeDisabled || isPlatformDisabled}
          tradingAccount={tradingAccount}
        >
          <Button.Primary size='sm'>{t('Trade')}</Button.Primary>
        </TradeButtonLinkWrapper>
        {isPlatformDisabled && (
          <ActionIcon
            icon={InfoIcon}
            size='x16'
            onClick={() => handleWebTraderModalVisibility(tradingAccount)}
          />
        )}
      </Box>
      <Tooltip
        disabled={!isDepositDisabled}
        content={t('Trading Account.Deposits for this trading account are unavailable')}
      >
        <Button.Secondary size='sm' disabled={isDepositDisabled} onClick={handleDeposit}>
          {t('Deposit')}
        </Button.Secondary>
      </Tooltip>
      <TradingAccountActionsDropdown
        tradingAccount={tradingAccount}
        restrictions={hasRestrictions(tradingAccount) ? tradingAccount.restrictions : []}
        onChangePasswordRestrictedClick={handleChangePasswordRestricted}
        onChangeInvestorPasswordRestrictedClick={handleChangeInvestorPasswordRestricted}
        isTransferFromRestricted={isTransferFromRestricted}
        onTransferClick={handleTransfer}
        onResetPasswordClick={handleResetPassword}
        onResetPasswordRestrictedClick={handleResetPasswordRestricted}
      />
    </Box>
  )
}

interface AccountDetailsProps {
  tradingAccount: TradingAccount
  balance: string
  hasCampaign: boolean
  isInternalOnlyCampaign: boolean
  isMT4: boolean
  isMT5: boolean
  hasAllRestrictions: boolean
  sensitiveDataFlag: boolean
  onLeverageChange?: (args: { tradingAccount: TradingAccount; restricted?: boolean }) => void
  onDisplayDynamicLeverageInfo?: () => void
  openCampaignModal: () => void
  navigate: ReturnType<typeof useNavigate>
}

const AccountDetails: FC<AccountDetailsProps> = ({
  tradingAccount,
  balance,
  hasCampaign,
  isInternalOnlyCampaign,
  isMT4,
  isMT5,
  hasAllRestrictions,
  sensitiveDataFlag,
  onLeverageChange,
  onDisplayDynamicLeverageInfo,
  openCampaignModal,
  navigate,
}) => {
  const { t } = useTranslation()

  const { dispatchOpenChat } = useDispatchOpenChat()

  return (
    <Box className={styles.accountDetails}>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph wrap='nowrap'>
          {t('Trading Account.Account Type')}
        </Caption1>
        <Body2>
          <Box flex gap='x4'>
            {hasCampaign ? (
              <>
                {tradingAccount.accountType.name} {t('Trading Account.Campaign')}
                {!isInternalOnlyCampaign && (
                  <ActionIcon icon={InfoIcon} size='x16' onClick={openCampaignModal} />
                )}
              </>
            ) : (
              tradingAccount.accountType.name
            )}
          </Box>
        </Body2>
      </Box>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph wrap='nowrap'>
          {t('Trading Account.Leverage')}
        </Caption1>
        <Box flex gap='x4' align='center'>
          <Body2 isParagraph>
            <Link
              state={{ tradingAccountId: tradingAccount.id }}
              onClick={(e) => {
                e.stopPropagation()
                onLeverageChange?.({ tradingAccount, restricted: hasAllRestrictions })
              }}
            >
              {tradingAccount.leverageType.name}
            </Link>
          </Body2>
          {isMT5 && tradingAccount.platformType.dynamicLeverage && (
            <ActionIcon
              size='x16'
              icon={InfoIcon}
              onClick={(e) => {
                e.stopPropagation()
                onDisplayDynamicLeverageInfo?.()
              }}
            />
          )}
        </Box>
      </Box>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph wrap='nowrap'>
          {t('Trading Account.Equity')}
        </Caption1>
        <Body2 isParagraph>
          <SensitiveData>{balance}</SensitiveData>
        </Body2>
      </Box>
      {tradingAccount.isSwapFree && tradingAccount.swapFreeLevel && (
        <SwapFreeContainer tradingAccount={tradingAccount} onContactSupport={dispatchOpenChat} />
      )}
      <Box className={styles.detailItem}>
        <Caption1 isParagraph wrap='nowrap'>
          {t('Trading Account.Platform')}
        </Caption1>
        <Box flex gap='x4' align='center'>
          <Body2>{getDisplayPlatformName(tradingAccount.platformType)}</Body2>

          {isTMTPlatformType(tradingAccount.platformType.id) && (
            <Tooltip
              direction='bottom'
              content={
                <Body2>
                  {t(
                    'Trading Account.You can use your Tickmill Trader login credentials to access the TradingView platform'
                  )}
                </Body2>
              }
              primaryButton={{
                label: t('Trading Account.Download'),
                onClick: () =>
                  navigate('/dashboard/traders-room/platforms/tickmilltrader/webtrader'),
                size: 'sm',
              }}
            >
              <IconButton.Ghost icon={ArrowCircleIcon.Down} />
            </Tooltip>
          )}
          {isTradingViewPlatformType(tradingAccount.platformType.id) && (
            <Tooltip
              direction='bottom'
              content={
                <Body2>
                  {t(
                    'Trading Account.You can use your TradingView login credentials to access the Tickmill Trader Mobile App'
                  )}
                </Body2>
              }
              primaryButton={{
                label: t('Trading Account.Download'),
                onClick: () => navigate('/dashboard/traders-room/platforms/tradingview'),
                size: 'sm',
              }}
            >
              <IconButton.Ghost icon={ArrowCircleIcon.Down} />
            </Tooltip>
          )}
          {isMT4 && (
            <IconButton.Ghost
              icon={ArrowCircleIcon.Down}
              onClick={() => navigate('/dashboard/traders-room/platforms/mt4')}
            />
          )}
          {isMT5 && (
            <IconButton.Ghost
              icon={ArrowCircleIcon.Down}
              onClick={() => navigate('/dashboard/traders-room/platforms/mt5')}
            />
          )}
        </Box>
      </Box>
      {!(isMT4 || isMT5) && (
        <Box className={styles.detailItem}>
          <Caption1 isParagraph wrap='nowrap'>
            {t('Trading Account.Account Number')}
          </Caption1>
          <Box flex gap='x4' align='center'>
            <Body2 isParagraph>{tradingAccount.name}</Body2>
            <CopyLink value={tradingAccount.name}>
              <ActionIcon icon={CopyIcon} size='x16' />
            </CopyLink>
          </Box>
        </Box>
      )}

      {tradingAccount.externalId && (
        <Box className={styles.detailItem}>
          <Caption1 isParagraph wrap='nowrap'>
            {t('Trading Account.Username')}
          </Caption1>
          <Box flex gap='x4' align='center'>
            <Body2 isParagraph>
              {sensitiveDataFlag ? (
                <SensitiveData>{tradingAccount.externalId}</SensitiveData>
              ) : (
                tradingAccount.externalId
              )}
            </Body2>
            <CopyLink value={tradingAccount.externalId} disabled={sensitiveDataFlag}>
              <ActionIcon icon={CopyIcon} size='x16' />
            </CopyLink>
          </Box>
        </Box>
      )}

      {(isMT4 || isMT5) && (
        <Box className={styles.detailItem}>
          <Caption1 isParagraph wrap='nowrap'>
            {t('Trading Account.MT Login')}
          </Caption1>
          <Box flex gap='x4' align='center'>
            <Body2 isParagraph>{tradingAccount.platformOverview.login}</Body2>
            <CopyLink value={tradingAccount.platformOverview.login}>
              <ActionIcon icon={CopyIcon} size='x16' />
            </CopyLink>
          </Box>
        </Box>
      )}

      {(isMT4 || isMT5) && (
        <Box className={styles.detailItem}>
          <Caption1 isParagraph wrap='nowrap'>
            {t('Trading Account.Server')}
          </Caption1>
          <Box flex gap='x4' align='center'>
            <Body2 isParagraph>{tradingAccount.server.publicName}</Body2>
            <CopyLink value={tradingAccount.server.publicName}>
              <ActionIcon icon={CopyIcon} size='x16' />
            </CopyLink>
          </Box>
        </Box>
      )}
      {tradingAccount.introducingBroker?.name && (
        <Box className={styles.detailItem}>
          <Caption1 isParagraph wrap='nowrap'>
            {t('Trading Account.Referral Code')}
          </Caption1>
          <Box flex gap='x4' align='center'>
            <Body2 isParagraph>{tradingAccount.introducingBroker?.name}</Body2>
            <CopyLink value={tradingAccount.introducingBroker?.name}>
              <ActionIcon icon={CopyIcon} size='x16' />
            </CopyLink>
          </Box>
        </Box>
      )}
    </Box>
  )
}

interface TradingAccountCardProps {
  tradingAccount: TradingAccount
  wallet?: WalletDto
  index: number
  platformTypes: PlatformType[]
  formatMoney: (amount: number, currency: string) => string
  sensitiveDataFlag: boolean
  getPlatformTradeUrl: (
    tradingAccount: TradingAccount | DemoTradingAccount | undefined
  ) => Promise<string | undefined>
  handleWebTraderModalVisibility: (tradingAccount: TradingAccount) => void
  navigate: ReturnType<typeof useNavigate>
  handleSetSelectedTradingAccount: (tradingAccount: TradingAccount) => void
  openResetPasswordModal: () => void
  handleChangePasswordRestricted: (tradingAccount: TradingAccount) => void
  handleResetPasswordRestricted: (tradingAccount: TradingAccount) => void
  handleChangeInvestorPasswordRestricted: (tradingAccount: TradingAccount) => void
  openCampaignModal: () => void
  onLeverageChange?: (args: { tradingAccount: TradingAccount; restricted?: boolean }) => void
  onDisplayDynamicLeverageInfo?: () => void
}

const TradingAccountCard: FC<TradingAccountCardProps> = ({
  tradingAccount,
  wallet,
  index,
  platformTypes,
  formatMoney,
  sensitiveDataFlag,
  getPlatformTradeUrl,
  handleWebTraderModalVisibility,
  navigate,
  handleSetSelectedTradingAccount,
  openResetPasswordModal,
  handleChangePasswordRestricted,
  handleResetPasswordRestricted,
  handleChangeInvestorPasswordRestricted,
  openCampaignModal,
  onLeverageChange,
  onDisplayDynamicLeverageInfo,
}) => {
  const computedData = useMemo(() => {
    const isTickmillPlatformTrader = isTMTPlatformType(tradingAccount.platformType.id)
    const hasAllRestrictions = isTradingAccountSuspended(tradingAccount.restrictions)
    const isTradeDisabled =
      !isWebTraderAvailable(tradingAccount) &&
      !isTradingAccountStatusCloseOnly(tradingAccount.status)
    const isMT4 = isMT4PlatformType(tradingAccount.platformType.id)
    const isMT5 = isMT5PlatformType(tradingAccount.platformType.id)
    const hasCampaign = !!tradingAccount.campaigns.length
    const isInternalOnlyCampaign = tradingAccount.campaigns.some((c) => c.internalOnly)
    const isStatusRestricted = isTradingAccountStatusCloseOnly(tradingAccount.status)
    const isTransferFromRestricted =
      isRestricted(TradingAccountRestrictions.TRANSFER_FROM, tradingAccount.restrictions) ||
      isStatusRestricted
    const isDepositDisabled =
      isWalletRestricted(WalletRestrictions.DEPOSIT, wallet?.restrictions || []) ||
      isStatusRestricted

    const isPlatformDisabled =
      platformTypes && isPlatformTypeDisabled(platformTypes, tradingAccount.platformType.id)
    const referralCode = tradingAccount.introducingBroker?.name
    const equity = formatMoney(tradingAccount.platformOverview.equity, tradingAccount.currency.id)
    const balance = formatMoney(tradingAccount.platformOverview.balance, tradingAccount.currency.id)

    return {
      isTickmillPlatformTrader,
      hasAllRestrictions,
      isTradeDisabled,
      isDepositDisabled,
      isMT4,
      isMT5,
      hasCampaign,
      isInternalOnlyCampaign,
      isTransferFromRestricted,
      isPlatformDisabled,
      referralCode,
      equity,
      balance,
    }
  }, [tradingAccount, platformTypes, formatMoney])

  const handleChangePasswordRestrictedBound = () => handleChangePasswordRestricted(tradingAccount)
  const handleResetPasswordRestrictedBound = () => handleResetPasswordRestricted(tradingAccount)
  const handleChangeInvestorPasswordRestrictedBound = () =>
    handleChangeInvestorPasswordRestricted(tradingAccount)

  return (
    <Box className={styles.cardWrapper} key={index}>
      <Box className={styles.card} gap='x16'>
        <Box className={styles.title}>
          <AccountHeader
            tradingAccount={tradingAccount}
            balance={computedData.balance}
            hasAllRestrictions={computedData.hasAllRestrictions}
            hasCampaign={computedData.hasCampaign}
            isPlatformDisabled={computedData.isPlatformDisabled}
            isTradeDisabled={computedData.isTradeDisabled}
          />
          <AccountActions
            tradingAccount={tradingAccount}
            isTradeDisabled={computedData.isTradeDisabled}
            isDepositDisabled={computedData.isDepositDisabled}
            isPlatformDisabled={computedData.isPlatformDisabled}
            hasCampaign={computedData.hasCampaign}
            isTransferFromRestricted={computedData.isTransferFromRestricted}
            hasAllRestrictions={computedData.hasAllRestrictions}
            getPlatformTradeUrl={getPlatformTradeUrl}
            handleWebTraderModalVisibility={handleWebTraderModalVisibility}
            navigate={navigate}
            handleSetSelectedTradingAccount={handleSetSelectedTradingAccount}
            openResetPasswordModal={openResetPasswordModal}
            handleChangePasswordRestricted={handleChangePasswordRestrictedBound}
            handleResetPasswordRestricted={handleResetPasswordRestrictedBound}
            handleChangeInvestorPasswordRestricted={handleChangeInvestorPasswordRestrictedBound}
          />
        </Box>
        <Box className={styles.divider}></Box>
        <AccountDetails
          tradingAccount={tradingAccount}
          balance={computedData.balance}
          hasCampaign={computedData.hasCampaign}
          isInternalOnlyCampaign={computedData.isInternalOnlyCampaign}
          isMT4={computedData.isMT4}
          isMT5={computedData.isMT5}
          hasAllRestrictions={computedData.hasAllRestrictions}
          sensitiveDataFlag={sensitiveDataFlag}
          onLeverageChange={onLeverageChange}
          onDisplayDynamicLeverageInfo={onDisplayDynamicLeverageInfo}
          openCampaignModal={openCampaignModal}
          navigate={navigate}
        />
      </Box>
    </Box>
  )
}

export const LiveTradingAccountsCards: React.FC<LiveTradingAccountsCardsProps> = (props) => {
  const navigate = useNavigate()
  const { formatMoney } = useFormatNumber()
  const { getPlatformTradeUrl } = usePlatformTradeUrl()
  const sensitiveDataFlag = useSensitiveDataFlag()

  const {
    openWebTraderMT5IsUnavailableModal,
    openWebTraderMT4IsUnavailableModal,
    openWebTraderTickmillTraderIsUnavailableModal,
    handleSetSelectedTradingAccount,
    openCampaignModal,
    handleChangePasswordRestricted,
    handleResetPasswordRestricted,
    handleChangeInvestorPasswordRestricted,
    openResetPasswordModal,
  } = useOutletContext<TradingAccountsOutletCtx>()

  const {
    tableQuery: { data },
    platformTypes,
    wallets,
    onLeverageChange,
    onDisplayDynamicLeverageInfo,
  } = props

  const handleWebTraderModalVisibility = (tradingAccount: TradingAccount) => {
    switch (tradingAccount.platformType.id) {
      case PlatformTypeEnum.MT4:
        return openWebTraderMT4IsUnavailableModal()
      case PlatformTypeEnum.MT5:
        return openWebTraderMT5IsUnavailableModal()
      default:
        return openWebTraderTickmillTraderIsUnavailableModal()
    }
  }

  return (
    <Box className={styles.cards}>
      {!data.length ? (
        <CreateTradingAccountCTA createTradingAccountPath={CommonPaths.NewLiveTradingAccount} />
      ) : (
        data.map((tradingAccount, index) => (
          <React.Fragment key={tradingAccount.id}>
            <TradingAccountCard
              tradingAccount={tradingAccount}
              wallet={wallets.find((x) => x.id === tradingAccount.wallet.id)}
              index={index}
              platformTypes={platformTypes}
              formatMoney={formatMoney}
              sensitiveDataFlag={sensitiveDataFlag}
              getPlatformTradeUrl={getPlatformTradeUrl}
              handleWebTraderModalVisibility={handleWebTraderModalVisibility}
              navigate={navigate}
              handleSetSelectedTradingAccount={handleSetSelectedTradingAccount}
              openResetPasswordModal={openResetPasswordModal}
              handleChangePasswordRestricted={handleChangePasswordRestricted}
              handleResetPasswordRestricted={handleResetPasswordRestricted}
              handleChangeInvestorPasswordRestricted={handleChangeInvestorPasswordRestricted}
              openCampaignModal={openCampaignModal}
              onLeverageChange={onLeverageChange}
              onDisplayDynamicLeverageInfo={onDisplayDynamicLeverageInfo}
            />
          </React.Fragment>
        ))
      )}
    </Box>
  )
}
